import { useMemo, useState } from 'react';
import type { Bucket, Command, Perspective, ProjectRole } from '@integrate/contracts';
import { BUCKETS, canPropose, canWrite, FREEZES_UNDER_PERIMETER } from '@integrate/contracts';
import {
  bucketAssignments,
  bucketableIds,
  bulkArmPolicy,
  editableFields,
  fieldSuggestions,
  fieldEditableIds,
  fmt,
  fmtMoney,
  selectionRollup,
  type ChartNode,
  type SeparationState,
} from '@integrate/core';
import { ICON, IconButton } from '../ui/Icon.js';
import { Section } from './sections.js';

const MEMBER_CAP = 60;

/**
 * What a bulk action does for this reader, as one function over the four roles.
 *
 * EXTRACTED RATHER THAN INLINED IN THE CALLER, because it is the decision this milestone is about and a
 * ternary inside JSX can only be exercised by driving a d3 chart to a selection — which is precisely the
 * kind of untestable-in-practice wiring that let `createProposal` sit uncalled through four milestones.
 * Here all four roles are one cheap assertion.
 *
 *   owner, editor  → apply    they may write, so they do
 *   reviewer       → propose  may not write, has everything to say; the whole reason the role exists
 *   viewer, none   → none     "read and export, changes nothing" is the role, and that is correct
 *
 * Asked of `canWrite` and `canPropose` rather than by comparing role strings, so a role added later gets
 * whichever answer those two already give it instead of silently falling to `none`.
 */
export function bulkIntent(role: ProjectRole | null | undefined): 'apply' | 'propose' | 'none' {
  if (!role) return 'none';
  if (canWrite(role)) return 'apply';
  return canPropose(role) ? 'propose' : 'none';
}

/**
 * What is selected, and the two bulk actions that can be aimed at it.
 *
 * Ported from `openSelectionPanel` (orgchart.html:4886–5001), with the frozen panel's own arithmetic —
 * FTE and salary over the filled headcount, the headline and the bucket tally over everybody, so each
 * figure sums to the number printed beside it. That asymmetry is deliberate and is why this does not reuse
 * the group panel's tally block, which filters both because it sits under a headcount row that filters.
 *
 * Three things it does that the original does not, each closing a defect rather than adding a feature:
 *
 * · It acts on the RESOLVED selection — the people this view is drawing — and says how many it is not
 *   acting on. The frozen panel resolves its population once when it opens and never again, so changing
 *   perspective left a live "No longer required" button bound to people no longer on screen, with no
 *   further interaction needed.
 * · Undecided is its own tally row. The frozen panel reads an absent decision as Remain, so "→ Remain"
 *   over a team looks like a no-op and is a permanent state change that opts every one of them out of the
 *   perimeter rule.
 * · A destructive bulk arms before it fires. The app gates a job-title edit at 26 people and gates
 *   No-longer-required — which drops the stage metadata and the stranded plan of everyone it touches — at
 *   nothing at all.
 *
 * Read-only about itself: it takes `onDispatch` for the bulks, and never `setLens`. Which people are
 * selected is the caller's state; this panel only reports and acts.
 */
export function SelectionPanel({
  state,
  nodes,
  selection,
  perspective,
  role,
  scenarioId,
  onRemove,
  onClear,
  onDispatch,
  onPropose,
  onSelectPerson,
  perimeterFrozen,
}: {
  state: SeparationState;
  nodes: readonly ChartNode[];
  selection: ReadonlySet<string>;
  /** Passed, not assumed: a field edit writes the base record on as-is and an overlay everywhere else. */
  perspective: Perspective;
  /**
   * WHO IS LOOKING, rather than what they are allowed to do.
   *
   * Replaces a `readOnly` boolean, and the reason is the P6 exit review's finding: a reviewer — the role
   * whose entire purpose is suggesting changes they may not make — met a disabled button here and had no
   * way to suggest anything from the app at all. `readOnly` conflated "may not change this" with "has
   * nothing to say about it", which is exactly the conflation `canComment` was split out to fix in P5ap.
   *
   * THE ROLE AND NOT THE DERIVED INTENT, deliberately, and a surviving mutation is why. When the caller
   * passed the intent, a mutation replacing `bulkIntent(role)` with the literal `'apply'` in `App.tsx`
   * survived every test here — `bulkIntent` was covered, and nothing asserted that anybody CALLED it.
   * Passing the role removes the seam instead of testing around it: there is no longer a derivation at
   * the call site to get wrong. `bulkIntent` stays exported, because the four-role answer is worth
   * naming and asserting on its own.
   */
  role: ProjectRole | null;
  scenarioId: string;
  onRemove(employeeId: string): void;
  onClear(): void;
  onDispatch(command: Command | readonly Command[]): void;
  /**
   * Raise these as a proposal rather than applying them. Resolves once it is on the record.
   *
   * Separate from `onDispatch` rather than a mode flag on it, because the two have genuinely different
   * shapes: a dispatch is fire-and-forget against the live design and reconciles through the socket, and
   * a proposal is a request whose ONLY feedback is that it succeeded. Sharing one callback would have
   * meant the panel could not tell the reader their suggestion had landed.
   */
  onPropose(command: Command | readonly Command[], title: string): Promise<void>;
  onSelectPerson(employeeId: string): void;
  /** The active scenario's governance gate — re-routes writers' perimeter bulks to proposals. */
  perimeterFrozen: boolean;
}): JSX.Element {
  const roll = useMemo(() => selectionRollup(state, nodes, selection), [state, nodes, selection]);
  const fields = useMemo(() => editableFields(state), [state]);
  const [fieldKey, setFieldKey] = useState(() => fields[0]?.key ?? '');
  const [value, setValue] = useState('');
  const [armed, setArmed] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [raised, setRaised] = useState('');

  const intent = bulkIntent(role);
  const readOnly = intent === 'none';
  const proposing = intent === 'propose';

  /**
   * One exit for both bulks, so "apply" and "propose" cannot drift apart.
   *
   * The COMMANDS ARE IDENTICAL either way — that is the point of the proposal mechanism and the reason
   * this is a branch at the last moment rather than two code paths. A reviewer's suggestion is the same
   * `setBucketBulk` an editor would have issued, which is what lets the applier run it unchanged under
   * their own name.
   *
   * A FROZEN PERIMETER re-routes a writer the same way, per command and from the same table the
   * server enforces with: a bucket bulk becomes a proposal (the recorded exception path) while a
   * field edit still applies, because the freeze governs the perimeter and not the whole design.
   */
  const submit = (command: Command, title: string): void => {
    const frozenOut = perimeterFrozen && intent === 'apply' && FREEZES_UNDER_PERIMETER[command.type];
    if (!proposing && !frozenOut) {
      onDispatch(command);
      return;
    }
    setRaised('');
    void onPropose(command, title).then(
      () =>
        setRaised(
          frozenOut
            ? `Suggested: ${title}. The perimeter is frozen, so this lands as a proposal for somebody else to apply.`
            : `Suggested: ${title}. An editor sees it under Proposals.`,
        ),
      (cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'That suggestion could not be recorded.'),
    );
  };

  const field = fields.find((f) => f.key === fieldKey);
  const suggestions = useMemo(
    () => (field && !field.numeric ? fieldSuggestions(state, fieldKey, perspective) : []),
    [state, fieldKey, perspective, field],
  );

  const editable = fieldEditableIds(roll.resolved);

  const applyBucket = (bucket: Bucket | null): void => {
    const { ids, skippedBuildUps } = bucketableIds(roll.resolved, bucket);
    if (!ids.length) {
      setError(
        skippedBuildUps
          ? 'Every person selected here is a planned hire — delete the position instead.'
          : 'Nothing in this view to move.',
      );
      return;
    }
    const key = `bucket:${bucket ?? 'undecided'}`;
    const policy = bulkArmPolicy('bucket', bucket, ids.length);
    if (policy.confirm && armed !== key) {
      setArmed(key);
      setError('');
      return;
    }
    /*
      One command, with each person's own `src`.

      A deliberate bulk IS the decision, so it should stamp 'manual' — but doing that to somebody the
      perimeter rule owns removes them from the slider's reach for good, and NOT doing it lets the next
      slider drag delete the whole move. Per assignment, one command says both, and the count is printed
      below rather than decided silently.
    */
    const { assignments } = bucketAssignments(state, ids, bucket);
    submit(
      { type: 'setBucketBulk', scenarioId, assignments },
      `Move ${fmt(ids.length)} ${ids.length === 1 ? 'person' : 'people'} to ${bucket ?? 'Undecided'}`,
    );
    setArmed(null);
    setError('');
  };

  const applyField = (): void => {
    // A blank is not a zero — the P4h-a rule. The frozen panel refuses one too.
    if (value.trim() === '') {
      setError('Enter a value.');
      return;
    }
    if (!editable.ids.length) {
      setError('Planned hires have no imported record to edit — change the position itself.');
      return;
    }
    const policy = bulkArmPolicy('field', null, editable.ids.length);
    if (policy.confirm && armed !== 'field') {
      setArmed('field');
      setError('');
      return;
    }
    submit(
      {
        type: 'setEmpFieldBulk',
        scenarioId,
        perspective,
        fieldKey,
        assignments: editable.ids.map((employeeId) => ({ employeeId, value })),
      },
      `Set ${field?.label ?? fieldKey} to "${value}" for ${fmt(editable.ids.length)} ${editable.ids.length === 1 ? 'person' : 'people'}`,
    );
    setArmed(null);
    setError('');
    setValue('');
  };

  const ruleOwned = useMemo(
    () => bucketAssignments(state, roll.resolved.map((p) => String(p.id)), null).ruleOwned,
    [state, roll.resolved],
  );

  return (
    <section data-testid="selection-panel">
      <h2>
        {fmt(roll.people)} {roll.people === 1 ? 'person' : 'people'} selected
      </h2>

      <div className="sp-row">
        <span className="k">People</span>
        <span className="v" data-testid="selection-count">
          {fmt(roll.people)}
          {roll.buildUps > 0 && (
            <span className="pill">
              incl. {fmt(roll.buildUps)} {roll.buildUps === 1 ? 'hire' : 'hires'}
            </span>
          )}
        </span>
      </div>
      <div className="sp-row">
        <span className="k">Total FTE</span>
        <span className="v">{fmt(roll.fte, 1)}</span>
      </div>
      <div className="sp-row" title="Raw annual salaries; open positions and inactive people carry none">
        <span className="k">Total salary</span>
        <span className="v">{fmtMoney(roll.salary)}</span>
      </div>
      {[...BUCKETS, 'undecided' as const].map((bucket) =>
        roll.bucketCounts[bucket] > 0 ? (
          <div className="sp-row" key={bucket}>
            <span className="k">{bucket === 'undecided' ? 'Undecided' : bucket}</span>
            <span className="v" data-testid={`selection-count-${String(bucket).replace(/\s+/g, '-')}`}>
              {fmt(roll.bucketCounts[bucket])}
            </span>
          </div>
        ) : null,
      )}

      {/*
        The line the frozen app never prints.

        A filter, a perspective or a stage can hide somebody who is still selected. Holding them is right —
        a lens must lose nothing, so clearing the filter brings them back — but ACTING on them is not, and
        the app does exactly that. Counted, named, and excluded from every number above and every command
        below.
      */}
      {roll.hiddenByLens > 0 && (
        <p
          className="muted"
          data-testid="selection-scope"
          data-selected-held={selection.size}
          data-selected-count={roll.people}
          data-selected-hidden={roll.hiddenByLens}
        >
          {fmt(roll.hiddenByLens)} of {fmt(selection.size)} selected {roll.hiddenByLens === 1 ? 'is' : 'are'}{' '}
          not in this view, so nothing here counts or changes {roll.hiddenByLens === 1 ? 'them' : 'them'}.
          Clear the filter or switch perspective to include {roll.hiddenByLens === 1 ? 'them' : 'them'} —
          the selection itself is untouched.
        </p>
      )}

      <div className="sp-btn-row">
        <button data-testid="selection-clear" onClick={onClear} style={{ width: '100%' }}>
          Clear selection
        </button>
      </div>

      <Section
        testId="selection-bulk"
        title="Bulk bucket assignment"
      >
        <p className="muted">
          Applies to the {fmt(roll.people)} in this view.
          {ruleOwned > 0 && (
            <>
              {' '}
              {fmt(ruleOwned)} of {roll.people === 1 ? 'them is' : 'them are'} currently owned by the
              perimeter rule and will stay that way, so the slider still governs {ruleOwned === 1 ? 'it' : 'them'};
              the rest become manual decisions.
            </>
          )}
        </p>
        <div className="sp-btn-row">
          {BUCKETS.map((bucket) => {
            const { ids, skippedBuildUps } = bucketableIds(roll.resolved, bucket);
            const key = `bucket:${bucket}`;
            return (
              <button
                key={bucket}
                data-testid={`selection-bulk-${bucket.replace(/\s+/g, '-')}`}
                data-armed={armed === key ? '1' : '0'}
                data-eligible={ids.length}
                data-skipped-buildups={skippedBuildUps}
                disabled={readOnly || !ids.length}
                title={
                  skippedBuildUps
                    ? `${fmt(skippedBuildUps)} planned ${skippedBuildUps === 1 ? 'hire is' : 'hires are'} skipped — delete the position instead`
                    : undefined
                }
                onClick={() => applyBucket(bucket)}
              >
                {armed === key
                  ? `Confirm: ${bucket} (${fmt(ids.length)})`
                  : proposing
                    ? `Suggest → ${bucket}`
                    : `→ ${bucket}`}
              </button>
            );
          })}
        </div>
        <div className="sp-btn-row">
          <button
            data-testid="selection-bulk-clear"
            disabled={readOnly || !roll.people}
            title="Remove the decision entirely, so the perimeter rule can reach them again"
            onClick={() => applyBucket(null)}
          >
            {proposing ? 'Suggest → Undecided' : '→ Undecided'}
          </button>
        </div>
      </Section>

      <Section
        testId="selection-field-edit"
        title="Bulk edit a field"
      >
        <p className="muted">
          {perspective === 'as-is'
            ? 'Changes the underlying data, for every scenario.'
            : `Writes a ${perspective} override in this scenario only.`}{' '}
          {editable.excludedBuildUps > 0 && (
            <span data-testid="selection-excluded-buildups" data-count={editable.excludedBuildUps}>
              {fmt(editable.excludedBuildUps)} planned{' '}
              {editable.excludedBuildUps === 1 ? 'hire is' : 'hires are'} excluded — a hire has no imported
              record, so edit the position itself.
            </span>
          )}
        </p>
        <div className="sp-row">
          <select
            data-testid="selection-field"
            value={fieldKey}
            disabled={readOnly}
            onChange={(e) => {
              setFieldKey(e.target.value);
              setValue('');
              setArmed(null);
            }}
          >
            {fields.map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
        <div className="sp-row">
          <input
            data-testid="selection-field-value"
            type={field?.numeric ? 'number' : 'text'}
            list="selection-field-list"
            placeholder="New value"
            value={value}
            disabled={readOnly}
            onChange={(e) => setValue(e.target.value)}
          />
          <datalist id="selection-field-list">
            {suggestions.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
          <button
            data-testid="selection-field-apply"
            data-armed={armed === 'field' ? '1' : '0'}
            data-eligible={editable.ids.length}
            disabled={readOnly || !editable.ids.length}
            onClick={applyField}
          >
            {armed === 'field'
              ? `Confirm (${fmt(editable.ids.length)})`
              : proposing
                ? 'Suggest'
                : 'Apply'}
          </button>
        </div>
        {error && (
          <p className="error" data-testid="selection-error">
            {error}
          </p>
        )}
        {raised && (
          /*
            A proposal's only feedback is that it landed. A dispatch shows itself — the chart moves — but
            a suggestion changes nothing on the reader's screen, so without this the reviewer presses a
            button and watches nothing happen, which reads as broken.
          */
          <p className="muted" data-testid="selection-proposed">
            {raised}
          </p>
        )}
      </Section>

      <Section
        testId="selection-members"
        title={
          <>
            Members <span className="pill">{fmt(roll.people)}</span>
          </>
        }
      >
        {roll.resolved.slice(0, MEMBER_CAP).map((person) => (
          <div className="sp-row" key={String(person.id)} data-testid="selection-member" data-employee-id={String(person.id)}>
            <button
              className="linkish k grow"
              style={{ textAlign: 'left' }}
              onClick={() => onSelectPerson(String(person.id))}
            >
              {person.name}
              {person.isBuildUp && <span className="pill">BU</span>}
            </button>
            <IconButton
              icon={ICON.remove}
              testId={`selection-remove-${String(person.id)}`}
              label="Remove from the selection"
              onClick={() => onRemove(String(person.id))}
            />
          </div>
        ))}
        {roll.people > MEMBER_CAP && (
          <p className="muted" data-testid="selection-capped">
            … and {fmt(roll.people - MEMBER_CAP)} more. Every bulk above applies to all {fmt(roll.people)},
            not just the ones listed.
          </p>
        )}
        <p className="muted">
          Ctrl/Cmd/Shift-click a person on the chart to extend or shrink the selection, or a group to take
          its whole team — the head included. The organisation&rsquo;s own card takes everyone.
        </p>
      </Section>
    </section>
  );
}
